import axios from 'axios';
import { supabase } from './supabase';
import { BACKEND_URL } from './config';
import { SUPABASE_URL } from '../auth/constants';

async function getValidAccessToken() {
    try {
        const projectRef = SUPABASE_URL.split('//')[1].split('.')[0];
        const storageKey = `sb-${projectRef}-auth-token`;
        const storedSession = localStorage.getItem(storageKey);
        
        if (storedSession) {
            try {
                const parsed = JSON.parse(storedSession);
                if (parsed?.access_token) {
                    return parsed.access_token;
                }
            } catch (e) {
                // Ignore parse errors
            }
        }
        
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('getSession timeout')), 5000)
        );
        
        try {
            const { data: { session }, error: sessionError } = await Promise.race([
                sessionPromise,
                timeoutPromise
            ]);
            
            if (sessionError) {
                return null;
            }
            
            if (session?.access_token) {
                return session.access_token;
            }
        } catch (timeoutError) {
            // Timeout handled
        }

        try {
            const refreshPromise = supabase.auth.refreshSession();
            const refreshTimeout = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('refreshSession timeout')), 3000)
            );
            const { data, error } = await Promise.race([refreshPromise, refreshTimeout]);
            
            if (!error && data.session?.access_token) {
                return data.session.access_token;
            }
        } catch (refreshError) {
            // Refresh failed
        }
        
        return null;
    } catch (error) {
        return null;
    }
}

// Custom error class for structured API errors
export class ApiError extends Error {
    constructor(errorData) {
        super(errorData.message || 'An error occurred');
        this.name = 'ApiError';
        this.code = errorData.code;
        this.message = errorData.message;
        this.details = errorData.details;
        this.field = errorData.field;
        this.timestamp = errorData.timestamp;
    }
}

async function makeAuthenticatedRequest(endpoint, options = {}) {
    const token = await getValidAccessToken();
    if (!token) {
        throw new Error('No access token available. Please log in again.');
    }

    const config = {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
        },
        timeout: options.timeout || 30000
    };

    try {
        const response = await axios({
            url: `${BACKEND_URL}${endpoint}`,
            ...config
        });
        
        const data = response.data;
        
        // Check for structured error response (success: false)
        if (data && data.success === false && data.error) {
            throw new ApiError(data.error);
        }
        
        // Return data (either response.data directly or response.data.data if success: true)
        return data.success === true ? data.data : data;
    } catch (error) {
        // Handle authentication errors with retry
        if (error.response?.status === 401 || error.response?.status === 403) {
            const refreshed = await supabase.auth.refreshSession();
            const newToken = refreshed.data?.session?.access_token;
            if (newToken) {
                config.headers['Authorization'] = `Bearer ${newToken}`;
                try {
                    const retryResponse = await axios({
                        url: `${BACKEND_URL}${endpoint}`,
                        ...config
                    });
                    const retryData = retryResponse.data;
                    
                    // Check for structured error response after retry
                    if (retryData && retryData.success === false && retryData.error) {
                        throw new ApiError(retryData.error);
                    }
                    
                    return retryData.success === true ? retryData.data : retryData;
                } catch (retryError) {
                    // If retry also fails, check for structured error
                    if (retryError.response?.data?.success === false && retryError.response?.data?.error) {
                        throw new ApiError(retryError.response.data.error);
                    }
                    throw retryError;
                }
            }
        }
        
        // If axios error has structured error response, throw ApiError
        if (error.response?.data?.success === false && error.response?.data?.error) {
            throw new ApiError(error.response.data.error);
        }
        
        // If error is already an ApiError, rethrow it
        if (error instanceof ApiError) {
            throw error;
        }
        
        // For other errors, wrap in a generic ApiError if possible
        if (error.response?.data) {
            throw new ApiError({
                code: 'INTERNAL_SERVER_ERROR',
                message: error.response.data.message || 'An unexpected error occurred',
                details: error.response.data.details || error.message,
                timestamp: new Date().toISOString()
            });
        }
        
        throw error;
    }
}

// Get VAPID public key (public endpoint, no auth required)
export const getVapidPublicKey = async () => {
    try {
        const response = await fetch(`${BACKEND_URL}/api/notifications/vapid-public-key`);
        if (!response.ok) {
            throw new Error(`Failed to fetch VAPID key (HTTP ${response.status})`);
        }
        const data = await response.json();
        const payload = data.success === true ? data.data : data;
        return payload.publicKey;
    } catch (error) {
        console.error('Error fetching VAPID key:', error);
        throw error;
    }
};

// Register web push subscription
export const registerPushSubscription = async (subscription) => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/push-subscription', {
            method: 'POST',
            data: { subscription }
        });
        return response;
    } catch (error) {
        console.error('Error registering push subscription:', error);
        throw error;
    }
};

// Unregister the current device's web push subscription
export const unregisterPushSubscription = async (endpoint) => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/push-subscription', {
            method: 'DELETE',
            data: { endpoint }
        });
        return response;
    } catch (error) {
        console.error('Error unregistering push subscription:', error);
        throw error;
    }
};

// Get followed channels synced from connected platforms (YouTube/Twitch)
export const getPlatformChannels = async () => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/platform-channels', {
            method: 'GET'
        });
        return response.channels || [];
    } catch (error) {
        console.error('Error fetching platform channels:', error);
        throw error;
    }
};

// Unlink a synced platform channel (it may reappear on the next follow re-sync)
// Mute or unmute live alerts for a synced platform channel (does not unlink)
export const updatePlatformChannelAlerts = async (channelId, alertsEnabled) => {
    try {
        const response = await makeAuthenticatedRequest(`/api/notifications/platform-channels/${channelId}`, {
            method: 'PATCH',
            data: { alertsEnabled }
        });
        return response;
    } catch (error) {
        console.error('Error updating platform channel alerts:', error);
        throw error;
    }
};

// Get all user subscriptions
export const getSubscriptions = async () => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/subscriptions', {
            method: 'GET'
        });
        return response.subscriptions || [];
    } catch (error) {
        console.error('Error fetching subscriptions:', error);
        throw error;
    }
};

// Subscribe to entity
export const subscribeToEntity = async (entityId, channels = ['web'], isMainEvent = false) => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/subscribe/entity', {
            method: 'POST',
            data: {
                entityId,
                channels,
                isMainEvent
            }
        });
        return response;
    } catch (error) {
        console.error('Error subscribing to entity:', {
            code: error.code,
            message: error.message,
            details: error.details,
            field: error.field
        });
        throw error;
    }
};

// Subscribe to event
export const subscribeToEvent = async (eventId, channels = ['web'], isMainEvent = false) => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/subscribe/event', {
            method: 'POST',
            data: {
                eventId,
                channels,
                isMainEvent
            }
        });
        return response;
    } catch (error) {
        console.error('Error subscribing to event:', {
            code: error.code,
            message: error.message,
            details: error.details,
            field: error.field
        });
        throw error;
    }
};

// Unsubscribe
export const unsubscribe = async (subscriptionId) => {
    try {
        const response = await makeAuthenticatedRequest(`/api/notifications/subscribe/${subscriptionId}`, {
            method: 'DELETE'
        });
        return response;
    } catch (error) {
        console.error('Error unsubscribing:', error);
        throw error;
    }
};

// Get notification preferences
export const getPreferences = async () => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/preferences', {
            method: 'GET'
        });
        return response;
    } catch (error) {
        console.error('Error fetching preferences:', error);
        throw error;
    }
};

// Update notification preferences
export const updatePreferences = async (preferences) => {
    try {
        const response = await makeAuthenticatedRequest('/api/notifications/preferences', {
            method: 'PUT',
            data: preferences
        });
        return response;
    } catch (error) {
        console.error('Error updating preferences:', error);
        throw error;
    }
};






